import { create } from 'zustand';

import { jwtDecode } from 'src/utils/isValidToken';

import { AxiosHttpClient } from 'src/http';
import { API_URL } from 'src/config-global';
import { AuthRepository } from 'src/repositories/auth/auth.repository';

type States = {
  isLoading: boolean;
  user?: UserType;
  cpf: string;
  deleteAccountFeedback?: boolean;
  userRedeemedBenefitFeedback?: boolean;
  rdSummitVersion?: string;
};

interface UserType {
  id: string | number | undefined;
  name?: string;
  nome?: string;
  email?: string;
  senha?: string;
  permissao?: string;
  status?: string;
  uf?: string;
  cpf?: string;
}

type postUserLoginParams = {
  cpf: string;
  setSession: (accessToken: string, refreshToken: string, user?: UserType) => void;
};

type Actions = {
  setUserData: (userData: UserType) => void;
  login: ({ cpf, setSession }: postUserLoginParams, BUId: string, eventId: string) => Promise<void>;
  register: (
    cpf: string,
    phone: string,
    name: string,
    email: string,
    setSession: any,
    BUId: string,
    eventId: string,
    lpEventsLocation?: {
      region?: string,
      uf?: string,
      city?: string
    }
  ) => Promise<void>;
  setDeleteAccountFeedback: (value: boolean) => void;
  setUserRedeemedBenefitFeedback: (value: boolean) => void;
  // postUserLogout: (clearSession: () => void) => Promise<void>;
  setRDSummitVersion: (value: string) => void;
};

type UserStore = Actions & States;

const initialValues: States = {
  isLoading: false,
  deleteAccountFeedback: false,
  userRedeemedBenefitFeedback: false,
  cpf: '',
  user: {
    id: 0,
    nome: '',
    email: '',
    senha: '',
    permissao: '',
    status: '',
  },
};

const api = new AxiosHttpClient(API_URL);

const repository = new AuthRepository(api);

export const useUserStore = create<UserStore>((set: any, get: any) => ({
  ...initialValues,

  setUserData: (userData: UserType) => {
    set({ user: userData })
  },

  login: async ({ cpf, setSession }: postUserLoginParams, BUId: string, eventId: string) => {
    cpf = cpf.replace(/\./g, '').replace(/-/g, '');
    set({ cpf });
    set({ isLoading: true });
    const response = await repository.authLogin(cpf, BUId, eventId);
    if (response.isLeft()) {
      set({ isLoading: false });
      throw response.value;
    }
    const { accessToken, refreshToken } = response.value;
    if (accessToken && refreshToken) {
      setSession(accessToken, refreshToken);
      set({ user: jwtDecode(accessToken).data })
      set({ rdSummitVersion: jwtDecode(accessToken)?.data?.rdSummitVersion })
    }
    set({ isLoading: false });
  },

  register: async (cpf: string, phone: string, name: string, email: string, setSession: any, BUId: string, eventId: string, lpEventsLocation?: { region?: string, uf?: string, city?: string }) => {
    cpf = cpf.replace(/\./g, '').replace(/-/g, '');
    set({ isLoading: true });
    const response = await repository.authRegister(cpf, name, email, phone, BUId, eventId, lpEventsLocation);
    if (response.isLeft()) {
      set({ isLoading: false });
      throw response.value;
    }
    const { accessToken, refreshToken } = response.value;
    if (accessToken && refreshToken) {
      setSession(accessToken, refreshToken);
      set({ rdSummitVersion: jwtDecode(accessToken)?.data?.rdSummitVersion })
    }
    set({ isLoading: false });
  },

  setDeleteAccountFeedback: (value: boolean) => {
    set({ deleteAccountFeedback: value });
  },

  setUserRedeemedBenefitFeedback: (value: boolean) => {
    set({ userRedeemedBenefitFeedback: value });
  },

  setRDSummitVersion: (value: string) => {
    set({ rdSummitVersion: value })
  },

}));
